import { useState } from "react";
import { SbNotification } from "uiCore";
import usePaicRedesignPlanStore, { useHasPaicRedesignPlanChanges } from "Store/usePaicRedesignPlanStore";
import { addTalentMobilityPaicRedesignPlan, updateTalentMobilityPaicRedesignPlan } from "Api/apiService";

// Save Plan / Save & Close Plan for PAIC Org Redesign — the same flow
// useRedeploymentPlanActions gives the redeployment screens, against
// usePaicRedesignPlanStore and the PAIC plan endpoints instead. Rendered via
// the shared PlanSaveButton on the version page and on PAIC's Reduction
// Detail entry (PaicReductionDetailV2).
//
//   - The first save POSTs a new plan; its plan_id/plan_name land back in the
//     store, so every later save PUTs an update to the same plan. The POST
//     carries the draft's client_request_id, so retrying a create whose
//     response was lost returns the same plan instead of a duplicate.
//   - "Save Plan" keeps the draft open, so the user can move on to other
//     versions of the same plan; "Save & Close Plan" also clears the draft
//     and fires onClosed.
//   - Both stay disabled until something differs from the last save (or
//     from the AI-proposed baseline before the first one) — hasDraft is
//     useHasPaicRedesignPlanChanges, and a successful save resets it.
const usePaicRedesignPlanActions = ({ onClosed } = {}) => {
  const planId = usePaicRedesignPlanStore((state) => state.planId);
  const planName = usePaicRedesignPlanStore((state) => state.planName);
  const hasDraft = useHasPaicRedesignPlanChanges();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isUpdate = Boolean(planId);
  const planLabel = isUpdate ? planName : "New Plan";

  // Returns true on success so PlanSaveButton can close its modal (left open
  // on failure, so the typed name isn't lost on a retry).
  const savePlan = async (rawPlanName, { closeAfter = false } = {}) => {
    if (!hasDraft) {
      SbNotification.info("Nothing to save", "There are no changes since the plan was last saved.");
      return false;
    }
    const trimmedPlanName = (rawPlanName || "").trim();
    if (!trimmedPlanName) {
      SbNotification.info("Plan name required", "Please enter a plan name.");
      return false;
    }

    const { payload, savedDraft } = usePaicRedesignPlanStore.getState().preparePlanSave(trimmedPlanName);

    setIsSubmitting(true);
    try {
      let response;
      if (isUpdate) {
        response = await updateTalentMobilityPaicRedesignPlan(planId, payload);
      } else {
        const isRetriedCreate = Boolean(usePaicRedesignPlanStore.getState().createRequestId);
        response = await addTalentMobilityPaicRedesignPlan({
          ...payload,
          client_request_id: usePaicRedesignPlanStore.getState().ensureCreateRequestId(),
        });
        // A retried create may get back the plan an earlier "failed" attempt
        // already stored — the backend ignores this payload then — so the
        // current draft is written onto it before it counts as saved.
        if (isRetriedCreate && response?.plan_id) {
          response = await updateTalentMobilityPaicRedesignPlan(response.plan_id, payload);
        }
      }
      const savedPlanName = response?.plan_name || trimmedPlanName;

      if (closeAfter) {
        usePaicRedesignPlanStore.getState().reset();
      } else {
        const store = usePaicRedesignPlanStore.getState();
        store.setPlan(response?.plan_id ?? planId, savedPlanName);
        store.markSaved(savedDraft);
      }

      SbNotification.success(
        isUpdate ? "PAIC redesign plan updated" : "PAIC redesign plan saved",
        `"${savedPlanName}" plan ${isUpdate ? "updated" : "saved"}.`
      );
      if (closeAfter) onClosed?.();
      return true;
    } catch (error) {
      SbNotification.error(
        isUpdate ? "Unable to update PAIC redesign plan" : "Unable to save PAIC redesign plan",
        error?.response?.data?.message || "Please try again."
      );
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { hasDraft, isSubmitting, planId, planName, planLabel, isUpdate, savePlan };
};

export default usePaicRedesignPlanActions;
